import { isNotNil } from '@lucidlink/tscore';
import { wrapper } from '@lucidlink/uicore';
import { disableForSuspendedPredicate, experimentalFeatureRoute, FeatureFlagGuard, FilespacePermissionRouteGuard, getRouteObject, guestLinkPrivateRoutePredicate, guestLoginRoutePredicate, guestPrivateRoutePredicate, HEADING_KEYS, IsMigratingRouteGuard, ManagementPlatformRoute, MemberRouteGuard, needsWorkspaceAuthenticationPredicate, PartnerMspUpgradeFeatureFlagProperties, privateRoutePredicate, PrivilegeRouteGuard, ProtectedRoute, RestrictedFilespaceRouteGuard, setHandle, UserMemberOfPartnerOrganizationRouteGuard, WorkspaceAuthenticationRouteGuard, WorkspaceStatusRouteGuard, } from '@lucidlink/workspacemanagement';
import { FilespaceFooterLayout, FullscreenLayout, HeaderLayout, WorkspaceNavigationLayout, WorkspaceSettingsLayout, } from '@lucidlink/workspacemanagement/router/components';
import { MANAGEMENT_PLATFORM_PATHS } from '#constants';
import { GuestHeaderLayout, ManagementPlatformHeaderLayout, } from './components';
export const webRouteObjects = [
    {
        lazy: () => import('#router/pages/Home.tsx'),
        path: 'home',
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
        ],
    },
    // DEV-ONLY hidden demo - registered only in experimental builds, linked from no nav
    experimentalFeatureRoute({
        lazy: () => import('#features/audit-demo/pages/AuditDemo/AuditDemo.tsx'),
        path: 'audit-demo',
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
        ],
    }),
    {
        lazy: () => import('#features/guest-links/pages/GuestLink/GuestLink.tsx'),
        path: 'g/:linkFormatVersion/:encodedFileData/:encodedEncryptedFileName',
        wrappers: [
            wrapper(ProtectedRoute, {
                predicateFn: guestPrivateRoutePredicate,
            }),
            wrapper(HeaderLayout),
            wrapper(FullscreenLayout),
        ],
    },
    {
        lazy: () => import('#features/guest-links/pages/GuestLinkLoginEmail/GuestLinkLoginEmail.tsx'),
        path: 'guest/:guestLinkId/login',
        wrappers: [
            wrapper(ProtectedRoute, {
                predicateFn: guestLoginRoutePredicate,
            }),
        ],
    },
    {
        lazy: () => import('#features/guest-links/pages/GuestLinkLoginCode/GuestLinkLoginCode.tsx'),
        path: 'guest/:guestLinkId/login/code',
        wrappers: [
            wrapper(ProtectedRoute, {
                predicateFn: guestLoginRoutePredicate,
            }),
        ],
    },
    {
        lazy: () => import('#features/guest-links/pages/GuestLinkLoginError/GuestLinkLoginError.tsx'),
        path: 'guest/:guestLinkId/login/error/:errorType',
        wrappers: [],
    },
    {
        lazy: () => import('#features/guest-links/pages/GuestBrowser/GuestBrowser.tsx'),
        path: 'guest/:guestLinkId',
        wrappers: [
            wrapper(ProtectedRoute, {
                predicateFn: guestLinkPrivateRoutePredicate,
            }),
            wrapper(GuestHeaderLayout),
            wrapper(FullscreenLayout),
        ],
    },
    {
        lazy: () => import('#features/guest-links/pages/LegacyGuestLogin/LegacyGuestLogin.tsx'),
        path: 'login-guest/:workspaceId',
        wrappers: [
            wrapper(ProtectedRoute, {
                predicateFn: guestLoginRoutePredicate,
            }),
        ],
    },
    {
        lazy: () => import('#features/guest-links/pages/VerifyAccountPrompt.tsx'),
        path: 'verify-account-guest',
        wrappers: [],
    },
    {
        lazy: () => import('#features/auth/pages/ZendeskAuth.tsx'),
        path: 'zendesk-auth',
        wrappers: [],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.billingPlans,
        }),
        lazy: () => import('#features/billing/pages/WorkspaceSubscriptionPlans/WorkspaceSubscriptionPlans.tsx'),
        path: 'workspaces/:workspaceId/settings/billing/plans',
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(PrivilegeRouteGuard, { requiredPrivileges: 'billing' }),
            wrapper(FullscreenLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.billingCheckout,
        }),
        lazy: () => import('#features/billing/pages/WorkspaceBillingCheckout/WorkspaceBillingCheckout.tsx'),
        path: 'workspaces/:workspaceId/settings/billing/checkout',
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(PrivilegeRouteGuard, { requiredPrivileges: 'billing' }),
            wrapper(FullscreenLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.billing,
        }),
        lazy: () => import('#features/billing/pages/WorkspaceSettingsBilling/WorkspaceSettingsBilling.tsx'),
        path: 'workspaces/:workspaceId/settings/billing',
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(PrivilegeRouteGuard, { requiredPrivileges: 'billing' }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.filespaceFiles,
        }),
        lazy: () => import('#features/filespaces/pages/FilespaceBrowser/FilespaceBrowser.tsx'),
        path: 'workspaces/:workspaceId/filespaces/:filespaceId/browser',
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(FilespaceFooterLayout),
            wrapper(RestrictedFilespaceRouteGuard),
        ],
    },
    {
        lazy: () => import('#features/management-platform/pages/ManagementPlatform.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.root,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerDashboard,
        }),
        lazy: () => import('#features/management-platform/pages/OrganizationDashboard/OrganizationDashboard.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.dashboard,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(ManagementPlatformHeaderLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerDetails,
        }),
        lazy: () => import('#features/management-platform/pages/OrganizationDetails/OrganizationDetails.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.details,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(ManagementPlatformHeaderLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerApiKey,
        }),
        lazy: () => import('#features/management-platform/pages/ApiKey/ApiKey.tsx'),
        path: 'management-platform/:contextOrgId/api-key',
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(ManagementPlatformHeaderLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerClient,
        }),
        lazy: () => import('#features/management-platform/pages/ClientDetails/ClientDetails.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.organization,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(ManagementPlatformHeaderLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerPrepareUpgrade,
        }),
        lazy: () => import('#features/management-platform/pages/PrepareUpgrade/PrepareUpgrade.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.organizationPrepareUpgrade,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(FullscreenLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerSetupFilespaceUpgrade,
        }),
        lazy: () => import('#features/management-platform/pages/SetupFilespaceUpgrade/SetupFilespaceUpgrade.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.organizationSetupFilespaceUpgrade,
        wrappers: [
            wrapper(FeatureFlagGuard, PartnerMspUpgradeFeatureFlagProperties),
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(FullscreenLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerViewFilespaceUpgrade,
        }),
        lazy: () => import('#features/management-platform/pages/ViewFilespaceUpgrade/ViewFilespaceUpgrade.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.organizationViewFilespaceUpgrade,
        wrappers: [
            wrapper(FeatureFlagGuard, PartnerMspUpgradeFeatureFlagProperties),
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(FullscreenLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerMigrateContract,
        }),
        lazy: () => import('#features/management-platform/pages/MigrateContract/MigrateContract.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.organizationMigrateContract,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(ManagementPlatformHeaderLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerClients,
        }),
        lazy: () => import('#features/management-platform/pages/PartnerClients/PartnerClients.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.organizationClients,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(ManagementPlatformHeaderLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerCreateContract,
        }),
        lazy: () => import('#features/management-platform/pages/CreateContract/CreateContract.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.organizationCreateContract,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(ManagementPlatformHeaderLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerUpsell,
        }),
        lazy: () => import('#features/management-platform/pages/Upsell/Upsell.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.organizationUpsell,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(ManagementPlatformHeaderLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.partnerPreviewContract,
        }),
        lazy: () => import('#features/management-platform/pages/PreviewContract/PreviewContract.tsx'),
        path: MANAGEMENT_PLATFORM_PATHS.organizationPreviewContract,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(ManagementPlatformRoute),
            wrapper(UserMemberOfPartnerOrganizationRouteGuard),
            wrapper(ManagementPlatformHeaderLayout),
        ],
    },
]
    .filter(isNotNil)
    .map((route) => getRouteObject(route));
